import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import JobCard from '@/components/JobCard';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Jobs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    localisation: searchParams.get('localisation') || '',
    domaine: searchParams.get('domaine') || '',
  });

  // Pagination state
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [perPage, setPerPage] = useState(parseInt(searchParams.get('per_page')) || 25);

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, page, perPage]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.localisation) params.append('localisation', filters.localisation);
      if (filters.domaine) params.append('domaine', filters.domaine);
      params.append('limit', String(perPage));
      params.append('skip', String((page - 1) * perPage));

      const response = await axios.get(`${API}/jobs?${params.toString()}`);
      // Backend now returns { jobs, total, ... } — keep backward compat with array
      if (Array.isArray(response.data)) {
        setJobs(response.data);
        setTotal(response.data.length);
      } else {
        setJobs(response.data.jobs || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des emplois');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    updateSearchParams(1, perPage);
  };

  const updateSearchParams = (newPage = page, newPerPage = perPage) => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.localisation) params.set('localisation', filters.localisation);
    if (filters.domaine) params.set('domaine', filters.domaine);
    if (newPage > 1) params.set('page', String(newPage));
    if (newPerPage !== 25) params.set('per_page', String(newPerPage));
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ search: '', localisation: '', domaine: '' });
    setPage(1);
    setSearchParams({});
  };

  const handlePerPageChange = (value) => {
    const n = parseInt(value);
    setPerPage(n);
    setPage(1);
    updateSearchParams(1, n);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
    updateSearchParams(p, perPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = filters.search || filters.localisation || filters.domaine;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  // Compute compact pagination items: 1 ... 5 6 [7] 8 9 ... 30
  const buildPageItems = () => {
    const items = new Set();
    items.add(1);
    items.add(totalPages);
    for (let i = page - 1; i <= page + 1; i++) {
      if (i >= 1 && i <= totalPages) items.add(i);
    }
    return [...items].sort((a, b) => a - b);
  };
  const pageItems = buildPageItems();

  const domaines = [
    'Technologie', 'Finance', 'Santé', 'Construction', 'Éducation',
    'Marketing', 'Ventes', 'Juridique', 'Transport', 'Administration'
  ];
  const localisations = ['Kinshasa', 'Lubumbashi', 'Goma', 'Matadi', 'Kolwezi'];

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            data-testid="jobs-page-title"
          >
            Offres d'emploi en RDC
          </h1>
          <p className="text-slate-600" data-testid="jobs-total-count">
            {loading ? 'Chargement...' : `${total} offre${total > 1 ? 's' : ''} disponible${total > 1 ? 's' : ''}`}
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Rechercher un emploi..."
                  className="h-12 pl-12"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  data-testid="jobs-search-input"
                />
              </div>

              <Select
                value={filters.localisation}
                onValueChange={(value) => setFilters({ ...filters, localisation: value === ' ' ? '' : value })}
              >
                <SelectTrigger className="w-full lg:w-48 h-12" data-testid="location-filter">
                  <SelectValue placeholder="Localisation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Toutes les villes</SelectItem>
                  {localisations.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.domaine}
                onValueChange={(value) => setFilters({ ...filters, domaine: value === ' ' ? '' : value })}
              >
                <SelectTrigger className="w-full lg:w-48 h-12" data-testid="domain-filter">
                  <SelectValue placeholder="Domaine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Tous les domaines</SelectItem>
                  {domaines.map((dom) => (
                    <SelectItem key={dom} value={dom}>{dom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                type="submit"
                className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-8"
                data-testid="jobs-search-button"
              >
                <Search className="h-5 w-5 lg:mr-2" />
                <span className="hidden lg:inline">Rechercher</span>
              </Button>
            </div>
          </form>

          {hasActiveFilters && (
            <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
              <div className="flex flex-wrap gap-2">
                {filters.search && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm">
                    Recherche: {filters.search}
                  </span>
                )}
                {filters.localisation && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm">
                    {filters.localisation}
                  </span>
                )}
                {filters.domaine && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm">
                    {filters.domaine}
                  </span>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-600" data-testid="clear-filters-button">
                <X className="h-4 w-4 mr-1" />
                Effacer les filtres
              </Button>
            </div>
          )}
        </div>

        {/* Per-page selector */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Afficher</span>
            <Select value={String(perPage)} onValueChange={handlePerPageChange}>
              <SelectTrigger className="w-24 h-9" data-testid="per-page-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>emplois par page</span>
          </div>
          {!loading && total > 0 && (
            <div className="text-sm text-slate-500" data-testid="page-info">
              Page {page} sur {totalPages}
            </div>
          )}
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
            <p className="mt-4 text-slate-600">Chargement des offres...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl" data-testid="no-jobs-message">
            <p className="text-xl text-slate-600 mb-2">Aucune offre trouvée</p>
            <p className="text-slate-500">Essayez de modifier vos critères de recherche</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="jobs-list">
              {jobs.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-1 flex-wrap" data-testid="pagination">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  data-testid="page-prev"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Précédent
                </Button>

                {pageItems.map((p, idx) => {
                  const prev = pageItems[idx - 1];
                  const showEllipsis = prev !== undefined && p - prev > 1;
                  return (
                    <React.Fragment key={p}>
                      {showEllipsis && <span className="px-2 text-slate-400">...</span>}
                      <Button
                        variant={p === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => goToPage(p)}
                        className={p === page ? 'bg-sky-500 hover:bg-sky-600 text-white' : ''}
                        data-testid={`page-${p}`}
                      >
                        {p}
                      </Button>
                    </React.Fragment>
                  );
                })}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                  data-testid="page-next"
                >
                  Suivant
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Jobs;
